import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { X, ArrowLeft, Check, Minus } from 'lucide-react';
import PublicHeader from './PublicHeader';
import PublicFooter from './PublicFooter';
import ScrollToTop from './ScrollToTop';
import WhatsAppButton from './WhatsAppButton';
import SEOHead from './SEOHead';
import { useVehicles } from '@/contexts/VehicleContext';
import { API, BACKEND_URL } from '@/config';

const getImageUrl = (imagePath) => {
  if (!imagePath) return imagePath;
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    return imagePath;
  }
  const vehicleImageMatch = imagePath.match(/\/uploads\/vehicle_images\/([^/]+)\/([^/]+)/);
  if (vehicleImageMatch) {
    return `${API}/images/${vehicleImageMatch[1]}/${vehicleImageMatch[2]}`;
  }
  const additionalImageMatch = imagePath.match(/\/uploads\/additional_images\/([^/]+)\/([^/]+)/);
  if (additionalImageMatch) {
    return `${API}/additional-images/${additionalImageMatch[1]}/${additionalImageMatch[2]}`;
  }
  return `${BACKEND_URL}${imagePath}`;
};

const ComparePage = () => {
  const navigate = useNavigate();
  const { compareList, removeFromCompare, clearCompare } = useVehicles();

  const formatPrice = (priceUsd) => {
    if (priceUsd) {
      return `$${priceUsd.toLocaleString()}`;
    }
    return 'N/A';
  };

  const comparisonSpecs = [
    { label: 'Year', key: 'year' },
    { label: 'Price', key: 'price', formatter: (v) => formatPrice(v.public_price_usd) },
    { label: 'Mileage', key: 'mileage', formatter: (v) => v.mileage ? `${v.mileage.toLocaleString()} km` : 'N/A' },
    { label: 'Fuel Type', key: 'fuel_type' },
    { label: 'Transmission', key: 'transmission' },
    { label: 'Body Type', key: 'body_type' },
    { label: 'Drive Type', key: 'drive_type' },
    { label: 'Engine', key: 'engine_cc', formatter: (v) => v.engine_cc ? `${v.engine_cc} cc` : 'N/A' },
    { label: 'Color', key: 'color' },
    { label: 'Doors', key: 'doors' },
    { label: 'Seats', key: 'seats' },
  ];

  if (compareList.length === 0) {
    return (
      <div className="min-h-screen bg-white">
        <SEOHead title="Compare Vehicles - Global Trade Car" />
        <PublicHeader currency={currency} onCurrencyChange={setCurrency} />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center py-20">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">No Vehicles to Compare</h2>
            <p className="text-gray-600 mb-8">Add vehicles to your comparison list to see them side-by-side</p>
            <Button onClick={() => navigate('/vehicles')} className="bg-red-600 hover:bg-red-700">
              Browse Vehicles
            </Button>
          </div>
        </div>
        
        <PublicFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <SEOHead title="Compare Vehicles - Global Trade Car" />
      <PublicHeader currency={currency} onCurrencyChange={setCurrency} />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <Button
              variant="ghost"
              onClick={() => navigate('/vehicles')}
              className="mb-4"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Vehicles
            </Button>
            <h1 className="text-3xl font-bold text-gray-900">Compare Vehicles</h1>
            <p className="text-gray-600 mt-2">Comparing {compareList.length} vehicle{compareList.length !== 1 ? 's' : ''}</p>
          </div>
          <Button
            variant="outline"
            onClick={clearCompare}
            className="text-red-600 hover:text-red-700 hover:bg-red-50"
          >
            Clear All
          </Button>
        </div>

        {/* Comparison Table */}
        <div className="overflow-x-auto">
          <table className="w-full bg-white rounded-lg shadow-sm">
            <thead>
              <tr className="border-b">
                <th className="p-4 text-left font-semibold text-gray-900 w-48">Specification</th>
                {compareList.map((vehicle) => (
                  <th key={vehicle.id} className="p-4 text-center">
                    <Card className="relative">
                      <button
                        onClick={() => removeFromCompare(vehicle.id)}
                        className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors z-10"
                      >
                        <X className="h-4 w-4" />
                      </button>
                      <CardContent className="p-4">
                        <div className="relative h-32 mb-3 rounded-lg overflow-hidden bg-gradient-to-br from-red-600 to-red-700">
                          {vehicle.images && vehicle.images[0] ? (
                            <img
                              src={getImageUrl(vehicle.images[0])}
                              alt={`${vehicle.year} ${vehicle.model}`}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="flex items-center justify-center h-full">
                              <Badge className="bg-yellow-500 text-white">Image Coming Soon</Badge>
                            </div>
                          )}
                        </div>
                        <h3 className="font-bold text-gray-900 mb-1">
                          {vehicle.year} {vehicle.brand || ''} {vehicle.model}
                        </h3>
                        <Button
                          size="sm"
                          onClick={() => navigate(`/vehicles/${vehicle.id}`)}
                          className="w-full mt-2"
                        >
                          View Details
                        </Button>
                      </CardContent>
                    </Card>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {comparisonSpecs.map((spec, index) => (
                <tr key={spec.key} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                  <td className="p-4 font-semibold text-gray-900">{spec.label}</td>
                  {compareList.map((vehicle) => {
                    const value = spec.formatter 
                      ? spec.formatter(vehicle)
                      : vehicle[spec.key] || 'N/A';
                    return (
                      <td key={vehicle.id} className="p-4 text-center text-gray-700">
                        {value}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Feature Comparison (if available) */}
        {compareList.some(v => v.features) && (
          <Card className="mt-8">
            <CardContent className="p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Features</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {compareList.map((vehicle) => (
                  <div key={vehicle.id}>
                    <h3 className="font-semibold text-gray-900 mb-3">
                      {vehicle.year} {vehicle.model}
                    </h3>
                    {vehicle.features ? (
                      <ul className="space-y-2">
                        {vehicle.features.split(',').map((feature, idx) => (
                          <li key={`${vehicle.id || 'vehicle'}-feature-${idx}`} className="flex items-start space-x-2 text-sm">
                            <Check className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                            <span className="text-gray-600">{feature.trim()}</span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-sm text-gray-500 italic">No features listed</p>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Actions */}
        <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
          <Button
            size="lg"
            onClick={() => navigate('/vehicles')}
            variant="outline"
          >
            Browse More Vehicles
          </Button>
          <Button
            size="lg"
            onClick={() => navigate('/contact')}
            className="bg-red-600 hover:bg-red-700"
          >
            Contact Us About These Vehicles
          </Button>
        </div>
      </div>

      <PublicFooter />
      <ScrollToTop />
      <WhatsAppButton />
    </div>
  );
};

export default ComparePage;
